import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  ArrowRight, ArrowUpDown, CheckCircle2, ChevronRight, Layers, Plus, Search, ShieldAlert,
  SlidersHorizontal, TrendingDown, X,
} from 'lucide-react'
import { Trail } from '../navigation/Trail.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { Link } from '../router.js'
import { Modal, apiGet, adminMutate, useToast, usePermissions } from '../admin/primitives.js'
import { Combobox, SegmentedControl, DatePicker, type ComboItem } from '../admin/pickers.js'
import { Field, TextInput, NumberInput } from '../admin/form.js'
import { humanName } from './format.js'
import { useMenuPersonne } from './menu-personne.js'
import './rh.css'        /* hub-hero__kicker / __sub, adm-note, gd-panel */
import './counters.css'

/**
 * SIRH LOT 4.6 — COMPTEURS, produit RH complet (docs/hr/counters-product-v1.md).
 *
 *   · /equipe/temps/compteurs            → HrCountersHomePage  (cockpit équipe)
 *   · /equipe/temps/compteurs/:personId  → HrCountersPersonPage (détail salarié)
 *   · fiche salarié, onglet Temps        → PersonCountersMini   (mini-synthèse)
 *
 * Tout ce qui s'affiche vient du read model (packages/core, accounts-read-
 * model.ts) : aucun calcul de solde ici, aucun chiffre inventé — un compteur
 * sans mouvement montre 0 et le dit. L'ajustement passe par l'endpoint
 * existant (/api/v1/hr/time/accounts/adjust), le solde initial par
 * /api/v1/hr/counters/opening-balance. Aucun select natif, aucune popup
 * navigateur : Combobox / SegmentedControl / DatePicker, toasts globaux.
 */

/* ─── Types du read model ─── */
type Unit = 'MINUTES' | 'DAYS'
interface TypeSummary { key: string; label: string; unit: Unit; description: string | null; tracked_persons: number; total_balance: number; negative_balances: number; movements_7d: number; last_movement_at: string | null }
interface PersonRow { person_id: string; display_name: string; position: string | null; balances: Record<string, number>; last_movement_at: string | null; has_account: boolean }
interface ToHandle { kind: 'NEGATIVE_BALANCE' | 'LARGE_ADJUSTMENT' | 'MISSING_OPENING_BALANCE'; person_id: string; display_name: string; type_key: string; detail: string; amount?: number }
interface Matrix { months: string[]; rows: Array<{ type_key: string; cells: Array<{ month: string; delta: number; balance_end: number }> }> }
interface Overview { types: TypeSummary[]; persons: PersonRow[]; to_handle: ToHandle[]; matrix: Matrix }
interface Movement {
  id: string; account_type_key: string; amount: number; kind: string; reason: string; source_type: string; source_id: string | null
  period_label: string | null; created_at: string; created_by_name: string | null; legal_article: string | null; approval_id: string | null
  origin: { kind: string; label: string; link: string | null }
}
interface Chrono { granularity: 'WEEK' | 'MONTH'; period: string; type_key: string; delta: number; balance_after: number }
interface PersonCounters {
  person: { id: string; display_name: string; position: string | null }
  accounts: Array<{ type_key: string; label: string; unit: Unit; balance: number; entries: number; last_movement_at: string | null; opening_recorded: boolean }>
  movements: Movement[]
  movements_total?: number | undefined
  movements_truncated?: boolean | undefined
  chronology: Chrono[]
  matrix: Matrix
}
type TypeInfo = { key: string; label: string; unit: Unit }

/* ─── Affichage ─── */
const PARIS = 'Europe/Paris'
const TONES: Record<string, string> = { RECUP: 'violet', CP: 'cyan', REPOS_COMPENSATEUR: 'emerald', FERIES: 'amber' }
const toneOf = (key: string): string => `ct-tone--${TONES[key] ?? 'blue'}`
const unitWord = (u: Unit): string => u === 'MINUTES' ? 'heures' : 'jours'

const DAYS_FMT = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 })

/** « 12 h 45 » / « −7 h » / « 3 j » / « 0,5 j » — signe « + » seulement si demandé. */
export function fmtAmount(unit: Unit, n: number, signed = false): string {
  const sign = n < 0 ? '−' : signed && n > 0 ? '+' : ''
  const abs = Math.abs(n)
  if (unit === 'MINUTES') {
    const total = Math.round(abs)
    const h = Math.floor(total / 60); const m = total % 60
    return `${sign}${h} h${m ? ` ${String(m).padStart(2, '0')}` : ''}`
  }
  return `${sign}${DAYS_FMT.format(abs)} j`
}
const fmtDate = (iso: string): string => new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: PARIS })
const fmtTime = (iso: string): string => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: PARIS })
const fmtYmd = (ymd: string): string => { const [y, m, d] = ymd.split('-'); return `${d}/${m}/${y}` }
const MONTHS_SHORT = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.']
const MONTHS_LONG = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']
const fmtMonth = (ym: string, long = false): string => { const [y, m] = ym.split('-'); return `${(long ? MONTHS_LONG : MONTHS_SHORT)[Number(m) - 1]} ${y}` }
const fmtPeriod = (p: string): string => /W/.test(p) ? `Semaine ${p.slice(6)} · ${p.slice(0, 4)}` : fmtMonth(p, true)
const todayYmd = (): string => new Intl.DateTimeFormat('fr-CA', { timeZone: PARIS, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date())
const currentMonth = (): string => todayYmd().slice(0, 7)
const numClass = (n: number): string => n < 0 ? 'ct-num ct-num--neg' : n === 0 ? 'ct-num ct-num--zero' : 'ct-num'

const KIND_LABELS: Record<string, string> = {
  OPENING_BALANCE: 'Solde initial', ACCRUAL: 'Acquisition', USAGE: 'Utilisation',
  ADJUSTMENT: 'Ajustement', LEGAL_ENTITLEMENT: 'Droit légal', EXPIRY: 'Expiration',
}
const TODO_LABELS: Record<ToHandle['kind'], string> = {
  NEGATIVE_BALANCE: 'Solde négatif', LARGE_ADJUSTMENT: 'Ajustement important', MISSING_OPENING_BALANCE: 'Solde initial manquant',
}

/** Qui a agi : automatique (validation, congé, règle) vs geste humain (ajustement, solde initial). */
function actorLine(m: Movement): string {
  const by = m.created_by_name ? humanName(m.created_by_name) : null
  if (m.source_type === 'MANUAL') return m.kind === 'OPENING_BALANCE' ? `Initialisé par ${by ?? 'un acteur inconnu'}` : `Ajusté par ${by ?? 'un acteur inconnu'}`
  if (m.source_type === 'IMPORT') return by ? `Importé par ${by}` : 'Importé'
  return by ? `Créé automatiquement · déclenché par ${by}` : 'Créé automatiquement'
}

/* Toast STABLE entre les renders : il entre dans les deps des useCallback de
   chargement — une identité neuve à chaque render créerait une boucle de
   requêtes (déjà vu en recette, TempsPages). */
function useNotify(): (kind: 'ok' | 'error', text: string, action?: { label: string; onAction: () => void }) => void {
  const raw = useToast()
  return useCallback((kind: 'ok' | 'error', text: string, action?: { label: string; onAction: () => void }) => raw(kind, text, action ? { action } : undefined), [raw])
}

function typeItems(types: TypeInfo[]): ComboItem[] {
  return types.map((t) => ({ value: t.key, label: t.label, hint: unitWord(t.unit), dot: toneOf(t.key) }))
}

/* ═══════════════════════════════ COCKPIT ═══════════════════════════════ */

type Sort = { key: string; dir: 1 | -1 }
function parseSort(s: string | null): Sort {
  if (!s) return { key: 'name', dir: 1 }
  return s.startsWith('-') ? { key: s.slice(1), dir: -1 } : { key: s, dir: 1 }
}

export function HrCountersHomePage() {
  const notify = useNotify()
  const perms = usePermissions()
  const menu = useMenuPersonne()
  const canAdjust = !perms.loading && perms.permissions.includes('time.account.adjust')
  const qs = new URLSearchParams(window.location.search)
  const [q, setQ] = useState(qs.get('q') ?? '')
  const [sort, setSort] = useState<Sort>(parseSort(qs.get('sort')))
  const [view, setView] = useState<'synthese' | 'matrice'>(qs.get('view') === 'matrice' ? 'matrice' : 'synthese')
  const [data, setData] = useState<Overview | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  const [adjusting, setAdjusting] = useState<{ personId?: string; typeKey?: string } | null>(null)
  const [opening, setOpening] = useState<{ personId?: string; typeKey?: string } | null>(null)

  const load = useCallback(() => {
    apiGet<Overview & { error?: string; message?: string }>('/api/v1/hr/counters/overview')
      .then((r) => { if ('types' in r && Array.isArray(r.types)) { setData(r); setFailed(null) } else setFailed(r.message ?? 'Compteurs indisponibles.') })
      .catch(() => { setFailed('Chargement impossible — le serveur ne répond pas.'); notify('error', 'Chargement des compteurs impossible.') })
  }, [notify])
  useEffect(() => { load() }, [load])

  // L'état de la page vit dans l'URL : SmartBack depuis un salarié retrouve recherche, tri et vue.
  // L'état d'historique ({ lcIdx }) appartient au routeur : on le conserve, sinon le retour intelligent retombe sur le repli.
  useEffect(() => {
    const p = new URLSearchParams()
    if (q) p.set('q', q)
    if (sort.key !== 'name' || sort.dir !== 1) p.set('sort', `${sort.dir < 0 ? '-' : ''}${sort.key}`)
    if (view !== 'synthese') p.set('view', view)
    const s = p.toString()
    window.history.replaceState(window.history.state, '', `/equipe/temps/compteurs${s ? `?${s}` : ''}`)
  }, [q, sort, view])

  const types: TypeInfo[] = useMemo(() => (data?.types ?? []).map((t) => ({ key: t.key, label: t.label, unit: t.unit })), [data])
  const typeByKey = useMemo(() => new Map(types.map((t) => [t.key, t])), [types])

  const persons = useMemo(() => {
    if (!data) return []
    const s = q.trim().toLowerCase()
    const list = s ? data.persons.filter((p) => p.display_name.toLowerCase().includes(s) || (p.position ?? '').toLowerCase().includes(s)) : [...data.persons]
    const cmp = (a: PersonRow, b: PersonRow): number => {
      if (sort.key === 'name') return a.display_name.localeCompare(b.display_name, 'fr')
      if (sort.key === 'last') return (a.last_movement_at ?? '').localeCompare(b.last_movement_at ?? '')
      return (a.balances[sort.key] ?? 0) - (b.balances[sort.key] ?? 0)
    }
    return list.sort((a, b) => sort.dir * cmp(a, b) || a.display_name.localeCompare(b.display_name, 'fr'))
  }, [data, q, sort])

  const toggleSort = (key: string) => setSort((s) => s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: key === 'name' ? 1 : -1 })
  const cols = `minmax(200px, 2.2fr) repeat(${types.length}, minmax(104px, 1fr)) minmax(120px, 1fr) 24px`
  const totalMovements7d = data?.types.reduce((n, t) => n + t.movements_7d, 0) ?? 0

  return (
    <main className="lc-page lc-page--wide lc-stagger">
      <Trail />
      <SmartBack fallback="/equipe/temps" />
      <header className="hub-hero ct-hero">
        <div>
          <p className="hub-hero__kicker">Temps</p>
          <h1 className="lc-title">Compteurs</h1>
          <p className="hub-hero__sub">Suivez les droits, récupérations et mouvements de temps de l’équipe. Un solde n’est jamais saisi : il est la somme de ses mouvements.</p>
          {data ? (
            <p className="ct-hero__meta">
              <span><strong>{data.persons.length}</strong> salarié{data.persons.length > 1 ? 's' : ''} suivi{data.persons.length > 1 ? 's' : ''}</span>
              <span>·</span>
              <span><strong>{totalMovements7d}</strong> mouvement{totalMovements7d > 1 ? 's' : ''} sur 7 jours</span>
              <span>·</span>
              <span>{data.to_handle.length ? <><strong>{data.to_handle.length}</strong> point{data.to_handle.length > 1 ? 's' : ''} à traiter</> : 'Rien à traiter'}</span>
            </p>
          ) : null}
        </div>
        <div className="ct-hero__actions">
          <SegmentedControl value={view} onChange={setView} ariaLabel="Vue" options={[{ value: 'synthese', label: 'Synthèse' }, { value: 'matrice', label: 'Matrice' }]} />
          {canAdjust ? (
            <>
              <button type="button" className="lc-btn" onClick={() => setOpening({})}>Initialiser un solde</button>
              <button type="button" className="lc-btn lc-btn--primary" onClick={() => setAdjusting({})}><SlidersHorizontal size={15} aria-hidden="true" /> Ajuster un compteur</button>
            </>
          ) : null}
        </div>
      </header>

      {failed ? (
        <section className="ct-section"><div className="ct-empty ct-empty--block">
          <p className="ct-empty__title">Compteurs indisponibles</p>
          <p className="ct-empty__text">{failed} Les soldes ne sont pas perdus : ils sont recalculés depuis le registre à chaque ouverture.</p>
          <div className="ct-empty__actions"><button type="button" className="lc-btn" onClick={load}>Réessayer</button></div>
        </div></section>
      ) : !data ? (
        <div className="ct-types" aria-busy="true">{[0, 1, 2, 3].map((i) => <div key={i} className="ct-skel ct-skel--card" />)}</div>
      ) : (
        <>
          <section className="ct-types" aria-label="Compteurs par type">
            {data.types.map((t) => <TypeCard key={t.key} t={t} />)}
          </section>

          {view === 'matrice' ? (
            <section className="ct-section">
              <div className="ct-section__head">
                <h2 className="ct-section__title"><Layers size={16} aria-hidden="true" /> Matrice mois × compteurs</h2>
                <span className="lc-hint">Mouvements du mois et solde cumulé en fin de mois, sur l’effectif suivi — 12 derniers mois.</span>
              </div>
              <MatrixTable matrix={data.matrix} types={typeByKey} />
            </section>
          ) : (
            <>
              <section className="ct-section">
                <div className="ct-section__head">
                  <h2 className="ct-section__title"><ShieldAlert size={16} aria-hidden="true" /> À traiter{data.to_handle.length ? <span className="ct-count">· {data.to_handle.length}</span> : null}</h2>
                  <span className="lc-hint">Uniquement des faits : soldes négatifs, ajustements importants (30 j), congés décomptés sans solde initial.</span>
                </div>
                {data.to_handle.length === 0 ? (
                  <div className="ct-empty ct-empty--ok"><CheckCircle2 size={18} aria-hidden="true" /> Rien à traiter — aucun solde négatif, aucun ajustement important.</div>
                ) : (
                  <div className="ct-todo">
                    {data.to_handle.map((h, i) => {
                      const t = typeByKey.get(h.type_key)
                      return (
                        <Link key={`${h.kind}:${h.person_id}:${h.type_key}:${i}`} href={`/equipe/temps/compteurs/${h.person_id}`} className="ct-todo__row"
                          {...menu.personne({ id: h.person_id, nom: h.display_name, lien: 'compteurs' })}>
                          <span className={`ct-todo__icon${h.kind === 'NEGATIVE_BALANCE' ? ' ct-todo__icon--neg' : ''}`} aria-hidden="true">{h.kind === 'NEGATIVE_BALANCE' ? <TrendingDown size={16} /> : <ShieldAlert size={16} />}</span>
                          <span className="ct-todo__who"><strong>{humanName(h.display_name)}</strong><span>{TODO_LABELS[h.kind]} · {t?.label ?? h.type_key}</span></span>
                          <span className="ct-todo__detail" title={h.detail}>{h.detail}</span>
                          <span className="ct-todo__amt">{h.amount !== undefined && t ? fmtAmount(t.unit, h.amount, true) : ''}</span>
                        </Link>
                      )
                    })}
                  </div>
                )}
              </section>

              <section className="ct-section">
                <div className="ct-section__head">
                  <h2 className="ct-section__title">Salariés <span className="ct-count">· {persons.length}{q ? ` / ${data.persons.length}` : ''}</span></h2>
                  <div className="ct-section__tools">
                    <label className="ct-search">
                      <Search size={15} aria-hidden="true" />
                      <input className="lc-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un salarié, un poste…" aria-label="Rechercher un salarié" />
                    </label>
                  </div>
                </div>
                {data.persons.length === 0 ? (
                  <div className="ct-empty ct-empty--block">
                    <p className="ct-empty__title">Aucun salarié dans votre périmètre</p>
                    <p className="ct-empty__text">Les compteurs se lisent par équipe : votre périmètre ne contient pour l’instant aucun salarié suivi. Les responsables d’équipe sont définis dans Organisation → Équipes.</p>
                    <div className="ct-empty__actions"><Link href="/equipe/organisation/equipes" className="lc-btn">Ouvrir les équipes</Link></div>
                  </div>
                ) : persons.length === 0 ? (
                  <div className="ct-empty ct-empty--block">
                    <p className="ct-empty__title">Aucun salarié ne correspond à « {q} »</p>
                    <p className="ct-empty__text">La recherche porte sur le nom et le poste principal.</p>
                    <div className="ct-empty__actions"><button type="button" className="lc-btn" onClick={() => setQ('')}><X size={14} aria-hidden="true" /> Effacer la recherche</button></div>
                  </div>
                ) : (
                  <div className="ct-list" style={{ ['--ct-cols' as string]: cols }}>
                    <div className="ct-list__head" role="row">
                      <SortBtn label="Salarié" k="name" sort={sort} onClick={toggleSort} />
                      {types.map((t) => <SortBtn key={t.key} label={t.label} k={t.key} sort={sort} onClick={toggleSort} num />)}
                      <SortBtn label="Dernier mouvement" k="last" sort={sort} onClick={toggleSort} num />
                      <span />
                    </div>
                    {persons.map((p) => (
                      <Link key={p.person_id} href={`/equipe/temps/compteurs/${p.person_id}`} className="ct-row"
                        {...menu.personne({ id: p.person_id, nom: p.display_name, lien: 'compteurs' })}>
                        <span className="ct-row__who"><strong>{humanName(p.display_name)}</strong>{p.position ? <span>{p.position}</span> : null}</span>
                        {types.map((t) => { const b = p.balances[t.key] ?? 0; return <span key={t.key} className={numClass(b)} data-label={t.label}>{fmtAmount(t.unit, b)}</span> })}
                        <span className="ct-row__last">{p.last_movement_at ? fmtDate(p.last_movement_at) : 'Aucun'}</span>
                        <ChevronRight size={15} className="ct-row__arrow" aria-hidden="true" />
                      </Link>
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </>
      )}

      {adjusting && data ? (
        <AdjustModal types={types} people={data.persons} initial={adjusting} onClose={() => setAdjusting(null)} onDone={() => { setAdjusting(null); load() }} />
      ) : null}
      {opening && data ? (
        <OpeningModal types={types} people={data.persons} initial={opening} onClose={() => setOpening(null)} onDone={() => { setOpening(null); load() }}
          onSwitchToAdjust={(personId, typeKey) => { setOpening(null); setAdjusting({ personId, typeKey }) }} />
      ) : null}
    </main>
  )
}

function SortBtn({ label, k, sort, onClick, num }: { label: string; k: string; sort: Sort; onClick: (k: string) => void; num?: boolean }) {
  const on = sort.key === k
  return (
    <button type="button" className={`ct-sort${on ? ' ct-sort--on' : ''}${num ? ' ct-sort--num' : ''}`} onClick={() => onClick(k)} aria-sort={on ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
      {label} <ArrowUpDown size={12} aria-hidden="true" />
    </button>
  )
}

/** Carte d'un type — zero state RÉEL : « 35 salariés · 0 h de solde · Aucun mouvement enregistré ». */
function TypeCard({ t }: { t: TypeSummary }) {
  const zero = t.total_balance === 0
  return (
    <article className={`ct-type ct-type--static ${toneOf(t.key)}`}>
      <div className="ct-type__head">
        <span className="ct-type__label"><span className="lc-dot" aria-hidden="true" />{t.label}</span>
        <span className="ct-type__unit">{unitWord(t.unit)}</span>
      </div>
      <div className={`ct-type__big${t.total_balance < 0 ? ' ct-type__big--neg' : zero ? ' ct-type__big--zero' : ''}`}>{fmtAmount(t.unit, t.total_balance)}</div>
      <p className="ct-type__facts">
        <span><strong>{t.tracked_persons}</strong> salarié{t.tracked_persons > 1 ? 's' : ''}</span>
        <span>·</span>
        <span>{zero ? `${fmtAmount(t.unit, 0)} de solde` : 'solde cumulé'}</span>
        <span>·</span>
        <span className={t.negative_balances ? 'ct-warn' : ''}><strong>{t.negative_balances}</strong> négatif{t.negative_balances > 1 ? 's' : ''}</span>
      </p>
      <div className="ct-type__foot">
        {t.movements_7d ? `${t.movements_7d} mouvement${t.movements_7d > 1 ? 's' : ''} sur 7 j` : 'Aucun mouvement sur 7 j'}
        {t.last_movement_at ? ` · dernier le ${fmtDate(t.last_movement_at)}` : ' · Aucun mouvement enregistré'}
      </div>
    </article>
  )
}

function MatrixTable({ matrix, types }: { matrix: Matrix; types: Map<string, TypeInfo> }) {
  const now = currentMonth()
  const any = matrix.rows.some((r) => r.cells.some((c) => c.delta !== 0 || c.balance_end !== 0))
  return (
    <>
      <div className="ct-matrix-wrap">
        <table className="ct-matrix">
          <thead><tr><th>Compteur</th>{matrix.months.map((m) => <th key={m} className={m === now ? 'ct-matrix__now' : ''}>{fmtMonth(m)}</th>)}</tr></thead>
          <tbody>
            {matrix.rows.map((r) => {
              const t = types.get(r.type_key)
              return (
                <tr key={r.type_key} className={toneOf(r.type_key)}>
                  <td><span className="ct-matrix__type"><span className="lc-dot" aria-hidden="true" />{t?.label ?? r.type_key}</span></td>
                  {r.cells.map((c) => (
                    <td key={c.month}>
                      <span className="ct-cell">
                        <span className={`ct-cell__delta ${c.delta === 0 ? 'ct-num ct-num--zero' : c.delta < 0 ? 'ct-num ct-num--neg' : 'ct-num ct-num--pos'}`}>{t ? fmtAmount(t.unit, c.delta, true) : c.delta}</span>
                        <span className="ct-cell__bal">solde {t ? fmtAmount(t.unit, c.balance_end) : c.balance_end}</span>
                      </span>
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {!any ? <p className="lc-hint" style={{ marginTop: 'var(--sp-3)' }}>Aucun mouvement sur les 12 derniers mois : la matrice se remplira avec les validations, congés approuvés, droits légaux et ajustements.</p> : null}
    </>
  )
}

/* ═══════════════════════════════ DÉTAIL SALARIÉ ═══════════════════════════════ */

type PersonTab = 'synthese' | 'mouvements' | 'chronologie'

export function HrCountersPersonPage({ personId }: { personId: string }) {
  const notify = useNotify()
  const perms = usePermissions()
  const menu = useMenuPersonne()
  const canAdjust = !perms.loading && perms.permissions.includes('time.account.adjust')
  const qs = new URLSearchParams(window.location.search)
  const initialTab = qs.get('tab')
  const [tab, setTab] = useState<PersonTab>(initialTab === 'mouvements' || initialTab === 'chronologie' ? initialTab : 'synthese')
  const [data, setData] = useState<PersonCounters | null>(null)
  const [failed, setFailed] = useState<{ title: string; text: string } | null>(null)
  const [adjusting, setAdjusting] = useState<{ typeKey?: string } | null>(null)
  const [opening, setOpening] = useState<{ typeKey?: string } | null>(null)

  const load = useCallback(() => {
    apiGet<PersonCounters & { error?: string; message?: string }>(`/api/v1/hr/counters/person/${personId}`)
      .then((r) => {
        if ('accounts' in r && Array.isArray(r.accounts)) { setData(r); setFailed(null); return }
        if (r.error === 'NOT_FOUND') setFailed({ title: 'Salarié inconnu', text: 'Aucune personne ne porte cet identifiant. Le lien est peut-être périmé.' })
        else if (r.error === 'OUT_OF_SCOPE' || r.error === 'FORBIDDEN') setFailed({ title: 'Hors de votre périmètre', text: r.message ?? 'Ces compteurs sont réservés au responsable de l’équipe et à la Direction.' })
        else setFailed({ title: 'Compteurs indisponibles', text: r.message ?? 'Le serveur n’a pas pu répondre.' })
      })
      .catch(() => { setFailed({ title: 'Compteurs indisponibles', text: 'Le serveur ne répond pas. Les soldes ne sont pas perdus : ils sont recalculés depuis le registre.' }); notify('error', 'Chargement des compteurs impossible.') })
  }, [personId, notify])
  useEffect(() => { load() }, [load])

  useEffect(() => {
    const p = new URLSearchParams()
    if (tab !== 'synthese') p.set('tab', tab)
    const s = p.toString()
    window.history.replaceState(window.history.state, '', `/equipe/temps/compteurs/${personId}${s ? `?${s}` : ''}`)
  }, [tab, personId])

  const types: TypeInfo[] = useMemo(() => (data?.accounts ?? []).map((a) => ({ key: a.type_key, label: a.label, unit: a.unit })), [data])
  const typeByKey = useMemo(() => new Map(types.map((t) => [t.key, t])), [types])
  const name = data ? humanName(data.person.display_name) : null

  return (
    <main className="lc-page lc-page--management lc-stagger">
      <Trail {...(name ? { leaf: name } : {})} />
      <SmartBack fallback="/equipe/temps/compteurs" label="Compteurs" />
      {failed ? (
        <section className="ct-section"><div className="ct-empty ct-empty--block">
          <p className="ct-empty__title">{failed.title}</p>
          <p className="ct-empty__text">{failed.text}</p>
          <div className="ct-empty__actions"><Link href="/equipe/temps/compteurs" className="lc-btn">Retour aux compteurs</Link></div>
        </div></section>
      ) : !data ? (
        <div className="ct-person-cards" aria-busy="true">{[0, 1, 2, 3].map((i) => <div key={i} className="ct-skel ct-skel--card" />)}</div>
      ) : (
        <>
          <header className="hub-hero ct-hero">
            <div>
              <p className="hub-hero__kicker">Compteurs</p>
              <h1 className="lc-title" {...menu.personne({ id: personId, nom: data.person.display_name, lien: 'compteurs' })}>{name}</h1>
              <p className="hub-hero__sub">{data.person.position ?? 'Poste principal non renseigné'} · {data.movements.length ? `${data.movements.length} mouvement${data.movements.length > 1 ? 's' : ''} au registre` : 'Aucun mouvement au registre'}</p>
            </div>
            {canAdjust ? (
              <div className="ct-hero__actions">
                <button type="button" className="lc-btn" onClick={() => setOpening({})}>Initialiser un solde</button>
                <button type="button" className="lc-btn lc-btn--primary" onClick={() => setAdjusting({})}><SlidersHorizontal size={15} aria-hidden="true" /> Ajuster</button>
              </div>
            ) : null}
          </header>

          <section className="ct-person-cards" aria-label="Soldes par compteur">
            {data.accounts.map((a) => (
              <article key={a.type_key} className={`ct-type ct-type--static ${toneOf(a.type_key)}`}>
                <div className="ct-type__head">
                  <span className="ct-type__label"><span className="lc-dot" aria-hidden="true" />{a.label}</span>
                  <span className="ct-type__unit">{unitWord(a.unit)}</span>
                </div>
                <div className={`ct-type__big${a.balance < 0 ? ' ct-type__big--neg' : a.balance === 0 ? ' ct-type__big--zero' : ''}`}>{fmtAmount(a.unit, a.balance)}</div>
                <p className="ct-type__facts"><span><strong>{a.entries}</strong> mouvement{a.entries > 1 ? 's' : ''}</span><span>·</span><span>{a.opening_recorded ? 'solde initial enregistré' : 'sans solde initial'}</span></p>
                <div className="ct-type__foot">{a.last_movement_at ? `Dernière écriture le ${fmtDate(a.last_movement_at)}` : 'Aucune écriture'}</div>
              </article>
            ))}
          </section>

          <div className="ct-tabs">
            <SegmentedControl value={tab} onChange={setTab} ariaLabel="Section" options={[{ value: 'synthese', label: 'Synthèse' }, { value: 'mouvements', label: 'Mouvements' }, { value: 'chronologie', label: 'Chronologie' }]} />
          </div>

          {tab === 'synthese' ? (
            <>
              <section className="ct-section">
                <div className="ct-section__head"><h2 className="ct-section__title">Douze derniers mois</h2><span className="lc-hint">Mouvements du mois · solde en fin de mois</span></div>
                <MatrixTable matrix={data.matrix} types={typeByKey} />
              </section>
              <section className="ct-section">
                <div className="ct-section__head"><h2 className="ct-section__title">Ce que dit le registre</h2></div>
                <dl className="ct-facts">
                  <div><dt>Mouvements</dt><dd>{(data.movements_total ?? data.movements.length) || 'Aucun'}</dd></div>
                  <div><dt>Premier mouvement</dt><dd>{data.movements.length ? fmtDate(data.movements[data.movements.length - 1]!.created_at) : '—'}</dd></div>
                  <div><dt>Dernier mouvement</dt><dd>{data.movements.length ? fmtDate(data.movements[0]!.created_at) : '—'}</dd></div>
                  <div><dt>Ajustements manuels</dt><dd>{data.movements.filter((m) => m.kind === 'ADJUSTMENT' && m.source_type === 'MANUAL').length || 'Aucun'}</dd></div>
                  {data.accounts.map((a) => (
                    <div key={a.type_key}><dt>{a.label}</dt><dd>{a.opening_recorded ? 'Solde initial enregistré' : a.entries ? 'Solde initial jamais enregistré — le solde part de 0' : 'Aucune écriture'}</dd></div>
                  ))}
                </dl>
              </section>
            </>
          ) : null}

          {tab === 'mouvements' ? (
            <MovementsSection data={data} types={types} canAdjust={canAdjust} onAdjust={() => setAdjusting({})} onOpening={() => setOpening({})} />
          ) : null}

          {tab === 'chronologie' ? <ChronologySection data={data} types={types} /> : null}
        </>
      )}

      {adjusting && data ? (
        <AdjustModal types={types} lock={{ id: data.person.id, name: name ?? data.person.display_name }} initial={{ personId: data.person.id, ...(adjusting.typeKey ? { typeKey: adjusting.typeKey } : {}) }}
          onClose={() => setAdjusting(null)} onDone={() => { setAdjusting(null); load() }} />
      ) : null}
      {opening && data ? (
        <OpeningModal types={types} lock={{ id: data.person.id, name: name ?? data.person.display_name }} initial={{ personId: data.person.id, ...(opening.typeKey ? { typeKey: opening.typeKey } : {}) }}
          onClose={() => setOpening(null)} onDone={() => { setOpening(null); load() }}
          onSwitchToAdjust={(_p, typeKey) => { setOpening(null); setAdjusting({ typeKey }) }} />
      ) : null}
    </main>
  )
}

function MovementsSection({ data, types, canAdjust, onAdjust, onOpening }: { data: PersonCounters; types: TypeInfo[]; canAdjust: boolean; onAdjust: () => void; onOpening: () => void }) {
  const [filter, setFilter] = useState<string | null>(null)
  const typeByKey = useMemo(() => new Map(types.map((t) => [t.key, t])), [types])
  const list = filter ? data.movements.filter((m) => m.account_type_key === filter) : data.movements
  return (
    <section className="ct-section">
      <div className="ct-section__head">
        <h2 className="ct-section__title">Mouvements <span className="ct-count">· {list.length}</span>{data.movements_truncated ? <span className="lc-hint"> — liste bornée aux {data.movements.length} derniers sur {data.movements_total ?? '?'} ; solde, chronologie et matrice restent complets</span> : null}</h2>
        <div className="ct-section__tools">
          <Combobox value={filter} onChange={setFilter} items={typeItems(types)} clearable placeholder="Tous les compteurs" ariaLabel="Filtrer par compteur" />
        </div>
      </div>
      {data.movements.length === 0 ? (
        <div className="ct-empty ct-empty--block">
          <p className="ct-empty__title">Aucun mouvement enregistré</p>
          <p className="ct-empty__text">Les compteurs se remplissent avec les validations d’heures, les congés approuvés et les droits légaux. Pour partir d’un solde connu (Skello, fiche de paie), initialisez d’abord un solde ; une correction ponctuelle passe par un ajustement motivé.</p>
          {canAdjust ? <div className="ct-empty__actions"><button type="button" className="lc-btn lc-btn--primary" onClick={onOpening}>Initialiser un solde</button><button type="button" className="lc-btn" onClick={onAdjust}>Ajuster un compteur</button></div> : null}
        </div>
      ) : list.length === 0 ? (
        <div className="ct-empty"><span>Aucun mouvement sur ce compteur.</span><button type="button" className="lc-btn lc-btn--ghost" onClick={() => setFilter(null)}>Tous les compteurs</button></div>
      ) : (
        <div className="ct-movs">
          {list.map((m) => {
            const t = typeByKey.get(m.account_type_key)
            return (
              <article key={m.id} className={`ct-mov ${toneOf(m.account_type_key)}`}>
                <div className="ct-mov__when">{fmtDate(m.created_at)}<span>{fmtTime(m.created_at)}</span></div>
                <div className={`ct-mov__amt ${numClass(m.amount)}${m.amount > 0 ? ' ct-num--pos' : ''}`}>{t ? fmtAmount(t.unit, m.amount, true) : m.amount}</div>
                <div className="ct-mov__body">
                  <div className="ct-mov__line">
                    <span className="ct-chip"><span className="lc-dot" aria-hidden="true" />{t?.label ?? m.account_type_key}</span>
                    <span className="ct-kind">{KIND_LABELS[m.kind] ?? m.kind}</span>
                    {m.period_label ? <span className="ct-kind">{/^\d{4}-\d{2}-\d{2}$/.test(m.period_label) ? `Effet au ${fmtYmd(m.period_label)}` : m.period_label}</span> : null}
                  </div>
                  <div className="ct-mov__reason">{m.reason}</div>
                  <div className="ct-mov__meta">
                    {m.origin.link ? <Link href={m.origin.link}>{m.origin.label} <ArrowRight size={12} aria-hidden="true" style={{ verticalAlign: '-1px' }} /></Link> : <span>{m.origin.label}</span>}
                    <span>·</span>
                    <span>{actorLine(m)}</span>
                    {m.legal_article ? <><span>·</span><span>{m.legal_article}</span></> : null}
                    {m.approval_id ? <><span>·</span><span>Approbation tracée</span></> : null}
                  </div>
                </div>
              </article>
            )
          })}
        </div>
      )}
    </section>
  )
}

function ChronologySection({ data, types }: { data: PersonCounters; types: TypeInfo[] }) {
  const [gran, setGran] = useState<'WEEK' | 'MONTH'>('MONTH')
  const rows = data.chronology.filter((c) => c.granularity === gran)
  const periods = [...new Set(rows.map((r) => r.period))].sort((a, b) => b.localeCompare(a))
  const used = types.filter((t) => rows.some((r) => r.type_key === t.key))
  return (
    <section className="ct-section">
      <div className="ct-section__head">
        <h2 className="ct-section__title">Chronologie <span className="ct-count">· {periods.length} {gran === 'WEEK' ? 'semaine' : 'mois'}{periods.length > 1 && gran === 'WEEK' ? 's' : ''}</span></h2>
        <SegmentedControl size="sm" value={gran} onChange={setGran} ariaLabel="Regroupement" options={[{ value: 'WEEK', label: 'Semaine' }, { value: 'MONTH', label: 'Mois' }]} />
      </div>
      {periods.length === 0 ? (
        <div className="ct-empty"><span>Aucune période : la chronologie apparaît dès le premier mouvement.</span></div>
      ) : (
        <div className="ct-matrix-wrap">
          <table className="ct-chrono">
            <thead><tr><th>Période</th>{used.map((t) => <th key={t.key}>{t.label}</th>)}</tr></thead>
            <tbody>
              {periods.map((p) => (
                <tr key={p}>
                  <td>{fmtPeriod(p)}</td>
                  {used.map((t) => {
                    const c = rows.find((r) => r.period === p && r.type_key === t.key)
                    return (
                      <td key={t.key}>
                        {c ? <span className="ct-cell"><span className={`ct-cell__delta ${numClass(c.delta)}${c.delta > 0 ? ' ct-num--pos' : ''}`}>{fmtAmount(t.unit, c.delta, true)}</span><span className="ct-cell__bal">solde {fmtAmount(t.unit, c.balance_after)}</span></span> : <span className="ct-num ct-num--zero">—</span>}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="lc-hint" style={{ marginTop: 'var(--sp-3)' }}>Regroupement sur la date d’enregistrement (Europe/Paris) ; le solde indiqué est celui après la période.</p>
    </section>
  )
}

/* ═══════════════════════════════ MODALES WORKFLOW ═══════════════════════════════ */

interface ModalCommon {
  types: TypeInfo[]
  people?: PersonRow[]
  /** Salarié verrouillé (page détail) : pas de combobox, le nom s'affiche. */
  lock?: { id: string; name: string }
  initial: { personId?: string; typeKey?: string }
  onClose: () => void
  onDone: () => void
}

function peopleItems(people: PersonRow[] | undefined): ComboItem[] {
  return (people ?? []).map((p) => ({ value: p.person_id, label: humanName(p.display_name), ...(p.position ? { hint: p.position } : {}) }))
}

/** Montant selon l'unité : heures + minutes (MINUTES) ou jours par demi-journée (DAYS). */
function AmountFields({ unit, h, m, d, onH, onM, onD }: { unit: Unit; h: string; m: string; d: string; onH: (v: string) => void; onM: (v: string) => void; onD: (v: string) => void }) {
  if (unit === 'MINUTES') {
    return (
      <div className="ct-modal__amount">
        <NumberInput value={h} onChange={onH} unit="h" min={0} max={999} ariaLabel="Heures" width={120} placeholder="0" />
        <NumberInput value={m} onChange={onM} unit="min" min={0} max={59} step={5} steppers ariaLabel="Minutes" width={170} placeholder="0" />
      </div>
    )
  }
  return <NumberInput value={d} onChange={onD} unit="j" min={0} max={365} step={0.5} steppers ariaLabel="Jours" width={170} placeholder="0" />
}

function useAmount(unit: Unit | undefined) {
  const [h, setH] = useState(''); const [m, setM] = useState(''); const [d, setD] = useState('')
  const abs = unit === 'MINUTES' ? (Number(h) || 0) * 60 + (Number(m) || 0) : Number(d) || 0
  return { h, m, d, setH, setM, setD, abs: Number.isFinite(abs) ? abs : 0 }
}

function AdjustModal(props: ModalCommon) {
  const notify = useNotify()
  const [personId, setPersonId] = useState<string | null>(props.lock?.id ?? props.initial.personId ?? null)
  const [typeKey, setTypeKey] = useState<string | null>(props.initial.typeKey ?? props.types[0]?.key ?? null)
  const [sign, setSign] = useState<'+' | '-'>('+')
  const [reason, setReason] = useState('')
  const [effectiveOn, setEffectiveOn] = useState(todayYmd())
  const [busy, setBusy] = useState(false)
  const type = props.types.find((t) => t.key === typeKey)
  const amt = useAmount(type?.unit)
  const signed = sign === '-' ? -amt.abs : amt.abs
  const [preview, setPreview] = useState<{ current_balance: number; adjustment: number; new_balance: number; unit: Unit } | null>(null)

  // Aperçu LIVE (débattu 250 ms) : solde réel + calcul pur côté serveur.
  useEffect(() => {
    if (!personId || !typeKey || signed === 0) { setPreview(null); return }
    let alive = true
    const t = setTimeout(() => {
      void adminMutate<{ error?: string; current_balance?: number; adjustment?: number; new_balance?: number; unit?: Unit }>('POST', '/api/v1/hr/counters/adjust/preview', { person_id: personId, type_key: typeKey, amount: signed })
        .then((r) => { if (alive) setPreview(r.status < 400 && r.data.unit ? { current_balance: r.data.current_balance ?? 0, adjustment: r.data.adjustment ?? 0, new_balance: r.data.new_balance ?? 0, unit: r.data.unit } : null) })
    }, 250)
    return () => { alive = false; clearTimeout(t) }
  }, [personId, typeKey, signed])

  const ready = !!personId && !!typeKey && signed !== 0 && reason.trim().length >= 3 && !busy
  async function submit() {
    if (!ready || !type) return
    setBusy(true)
    // `effective_on` est porté par period_label une fois le schéma hr-time câblé (voir docs) ; il est ignoré sinon.
    const r = await adminMutate<{ error?: string; message?: string }>('POST', '/api/v1/hr/time/accounts/adjust', { person_id: personId, type_key: typeKey, amount: signed, reason: reason.trim(), effective_on: effectiveOn })
    setBusy(false)
    if (r.status >= 400) { notify('error', r.data.message ?? 'Ajustement refusé.'); return }
    notify('ok', `Ajustement ${fmtAmount(type.unit, signed, true)} enregistré sur ${type.label} — motivé et audité.`)
    props.onDone()
  }

  return (
    <Modal open onClose={props.onClose} ariaLabel="Ajuster un compteur" width={600}>
      <div className="ct-modal">
        <h3>Ajuster un compteur</h3>
        <p className="ct-modal__lead">L’ajustement crée une écriture motivée et auditée dans le registre — le solde n’est jamais modifié directement, l’historique reste entier.</p>
        <div className="ct-modal__grid">
          <Field label="Salarié">
            {props.lock ? <span className="ct-locked">{props.lock.name}</span>
              : <Combobox value={personId} onChange={setPersonId} items={peopleItems(props.people)} placeholder="Choisir un salarié…" ariaLabel="Salarié" />}
          </Field>
          <Field label="Compteur">
            <Combobox value={typeKey} onChange={(v) => { setTypeKey(v); amt.setH(''); amt.setM(''); amt.setD('') }} items={typeItems(props.types)} placeholder="Choisir un compteur…" ariaLabel="Compteur" />
          </Field>
        </div>
        <div className="ct-modal__grid">
          <Field label="Sens">
            <SegmentedControl value={sign} onChange={setSign} ariaLabel="Sens de l’ajustement" options={[{ value: '+', label: 'Ajouter', icon: <Plus size={13} /> }, { value: '-', label: 'Retirer', icon: <TrendingDown size={13} /> }]} />
          </Field>
          <Field label={type ? `Montant (${unitWord(type.unit)})` : 'Montant'}>
            {type ? <AmountFields unit={type.unit} h={amt.h} m={amt.m} d={amt.d} onH={amt.setH} onM={amt.setM} onD={amt.setD} /> : <span className="lc-hint">Choisissez d’abord un compteur.</span>}
          </Field>
        </div>
        <Field label="Date d’effet" helper="Date métier du mouvement ; la date de saisie reste enregistrée à part.">
          <DatePicker value={effectiveOn} onPick={setEffectiveOn} label={fmtYmd(effectiveOn)} ariaLabel="Date d’effet" />
        </Field>
        <Field label="Motif (obligatoire)" helper="Visible par le salarié dans ses compteurs.">
          <TextInput value={reason} onChange={setReason} placeholder="ex. Heures du 12/09 non pointées, validées par la Direction" maxLength={300} />
        </Field>
        {type && signed !== 0 ? (
          preview ? (
            <div className={`ct-preview${preview.new_balance < 0 ? ' ct-preview--neg' : ''}`} role="status">
              <span>Solde actuel <strong>{fmtAmount(preview.unit, preview.current_balance, true)}</strong></span>
              <span className="ct-preview__arrow">·</span>
              <span>Ajustement <strong>{fmtAmount(preview.unit, preview.adjustment, true)}</strong></span>
              <span className="ct-preview__arrow">→</span>
              <span>Nouveau solde <strong className="ct-preview__new">{fmtAmount(preview.unit, preview.new_balance, true)}</strong></span>
              {preview.new_balance < 0 ? <span className="ct-preview__hint">Le solde deviendra négatif : il apparaîtra dans « À traiter ».</span> : null}
            </div>
          ) : <div className="ct-preview" role="status"><span>Calcul de l’aperçu…</span></div>
        ) : null}
        <div className="ct-modal__actions">
          <button type="button" className="lc-btn" onClick={props.onClose}>Annuler</button>
          <button type="button" className="lc-btn lc-btn--primary" disabled={!ready} onClick={() => void submit()}>{busy ? 'Enregistrement…' : 'Enregistrer l’ajustement'}</button>
        </div>
      </div>
    </Modal>
  )
}

function OpeningModal(props: ModalCommon & { onSwitchToAdjust: (personId: string, typeKey: string) => void }) {
  const notify = useNotify()
  const [personId, setPersonId] = useState<string | null>(props.lock?.id ?? props.initial.personId ?? null)
  const [typeKey, setTypeKey] = useState<string | null>(props.initial.typeKey ?? props.types[0]?.key ?? null)
  const [sign, setSign] = useState<'+' | '-'>('+')
  const [source, setSource] = useState<'IMPORT' | 'MANUAL'>('MANUAL')
  const [reason, setReason] = useState('')
  const [effectiveOn, setEffectiveOn] = useState(todayYmd())
  const [busy, setBusy] = useState(false)
  const type = props.types.find((t) => t.key === typeKey)
  const amt = useAmount(type?.unit)
  const signed = sign === '-' ? -amt.abs : amt.abs
  const ready = !!personId && !!typeKey && reason.trim().length >= 3 && !busy

  async function submit() {
    if (!ready || !type || !personId || !typeKey) return
    setBusy(true)
    const r = await adminMutate<{ error?: string; message?: string }>('POST', '/api/v1/hr/counters/opening-balance', { person_id: personId, type_key: typeKey, amount: signed, effective_on: effectiveOn, source, reason: reason.trim() })
    setBusy(false)
    if (r.status >= 400) {
      // Refus honnête : un solde initial existe déjà → on propose la bonne action.
      if (r.data.error === 'OPENING_BALANCE_REFUSED' && /existe déjà/.test(r.data.message ?? '')) {
        notify('error', r.data.message ?? 'Solde initial déjà enregistré.', { label: 'Ajuster plutôt', onAction: () => props.onSwitchToAdjust(personId, typeKey) })
      } else notify('error', r.data.message ?? 'Solde initial refusé.')
      return
    }
    notify('ok', `Solde initial ${fmtAmount(type.unit, signed)} enregistré sur ${type.label} (effet au ${fmtYmd(effectiveOn)}).`)
    props.onDone()
  }

  return (
    <Modal open onClose={props.onClose} ariaLabel="Initialiser un solde" width={600}>
      <div className="ct-modal">
        <h3>Initialiser un solde</h3>
        <p className="ct-modal__lead">Point de départ d’un compteur (reprise Skello, fiche de paie). Un seul solde initial par compteur : s’il existe déjà, on le corrige par un ajustement — jamais par un second solde initial.</p>
        <div className="ct-modal__grid">
          <Field label="Salarié">
            {props.lock ? <span className="ct-locked">{props.lock.name}</span>
              : <Combobox value={personId} onChange={setPersonId} items={peopleItems(props.people)} placeholder="Choisir un salarié…" ariaLabel="Salarié" />}
          </Field>
          <Field label="Compteur">
            <Combobox value={typeKey} onChange={(v) => { setTypeKey(v); amt.setH(''); amt.setM(''); amt.setD('') }} items={typeItems(props.types)} placeholder="Choisir un compteur…" ariaLabel="Compteur" />
          </Field>
        </div>
        <div className="ct-modal__grid">
          <Field label="Sens" helper="Un solde initial négatif est possible (droits pris par anticipation).">
            <SegmentedControl value={sign} onChange={setSign} ariaLabel="Signe du solde" options={[{ value: '+', label: 'Positif' }, { value: '-', label: 'Négatif' }]} />
          </Field>
          <Field label={type ? `Solde (${unitWord(type.unit)})` : 'Solde'} helper="0 est accepté : « solde constaté à zéro » est un fait enregistré.">
            {type ? <AmountFields unit={type.unit} h={amt.h} m={amt.m} d={amt.d} onH={amt.setH} onM={amt.setM} onD={amt.setD} /> : <span className="lc-hint">Choisissez d’abord un compteur.</span>}
          </Field>
        </div>
        <div className="ct-modal__grid">
          <Field label="Date d’effet">
            <DatePicker value={effectiveOn} onPick={setEffectiveOn} label={fmtYmd(effectiveOn)} ariaLabel="Date d’effet" />
          </Field>
          <Field label="Provenance">
            <SegmentedControl value={source} onChange={setSource} ariaLabel="Provenance" options={[{ value: 'MANUAL', label: 'Saisie manuelle' }, { value: 'IMPORT', label: 'Import (Skello…)' }]} />
          </Field>
        </div>
        <Field label="Motif (obligatoire)">
          <TextInput value={reason} onChange={setReason} placeholder="ex. Solde CP Skello au 31/08/2026" maxLength={300} />
        </Field>
        {type ? (
          <div className="ct-preview" role="status">
            <span>Le compteur <strong>{type.label}</strong> partira de <strong>{fmtAmount(type.unit, signed, true)}</strong> au {fmtYmd(effectiveOn)}.</span>
          </div>
        ) : null}
        <div className="ct-modal__actions">
          <button type="button" className="lc-btn" onClick={props.onClose}>Annuler</button>
          <button type="button" className="lc-btn lc-btn--primary" disabled={!ready} onClick={() => void submit()}>{busy ? 'Enregistrement…' : 'Enregistrer le solde initial'}</button>
        </div>
      </div>
    </Modal>
  )
}

/* ═══════════════════════════════ MINI-SYNTHÈSE (fiche salarié) ═══════════════════════════════ */

/** Onglet Temps de la fiche salarié (§90) : soldes par type + lien vers la page complète. */
export function PersonCountersMini({ personId }: { personId: string }) {
  // Un refus de permission n'est pas une panne : seul FORBIDDEN/OUT_OF_SCOPE parle de droits,
  // tout autre échec (404, 500, réseau) dit « indisponible » et se réessaie.
  const [state, setState] = useState<{ kind: 'loading' } | { kind: 'denied' } | { kind: 'failed' } | { kind: 'ok'; data: PersonCounters }>({ kind: 'loading' })
  const [attempt, setAttempt] = useState(0)
  useEffect(() => {
    setState({ kind: 'loading' })
    apiGet<PersonCounters & { error?: string }>(`/api/v1/hr/counters/person/${personId}`)
      .then((r) => {
        if ('accounts' in r && Array.isArray(r.accounts)) setState({ kind: 'ok', data: r })
        else if (r.error === 'FORBIDDEN' || r.error === 'OUT_OF_SCOPE') setState({ kind: 'denied' })
        else setState({ kind: 'failed' })
      })
      .catch(() => setState({ kind: 'failed' }))
  }, [personId, attempt])
  let body: ReactNode
  if (state.kind === 'loading') body = <p className="lc-hint">Chargement…</p>
  else if (state.kind === 'denied') body = <p className="adm-note">Les compteurs sont réservés à la permission dédiée (responsable d’équipe, Direction).</p>
  else if (state.kind === 'failed') body = <p className="adm-note">Compteurs indisponibles. <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => setAttempt((n) => n + 1)}>Réessayer</button></p>
  else {
    const d = state.data
    body = (
      <>
        <div className="ct-mini">
          {d.accounts.map((a) => (
            <div key={a.type_key} className={`ct-mini__item ${toneOf(a.type_key)}`}>
              <span className="ct-mini__label">{a.label}</span>
              <span className={`ct-mini__value${a.balance < 0 ? ' ct-num--neg' : ''}`}>{fmtAmount(a.unit, a.balance)}</span>
              <span className="ct-mini__label">{a.last_movement_at ? `Dernière écriture ${fmtDate(a.last_movement_at)}` : 'Aucune écriture'}</span>
            </div>
          ))}
        </div>
        <Link href={`/equipe/temps/compteurs/${personId}`} className="ct-mini__link">Voir tous les compteurs <ArrowRight size={14} aria-hidden="true" /></Link>
      </>
    )
  }
  return (
    <section className="lc-panel gd-panel" style={{ marginTop: 'var(--sp-4)' }}><div className="lc-panel__body">
      <h2 className="lc-section-title">Compteurs</h2>
      {body}
    </div></section>
  )
}
